import { useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import Link from "next/link";
import moment from "moment";

import Loader from "@/components/common/Loader/Loader";
import PageHeader from "@/components/PageHeader";
import Seo from "@/components/seo";
import { useNowPlaying } from "@/components/contexts/now-playing";
import styles from "@/styles/History.module.css";

/**
 * Groups the flat, most-recent-first history feed into day buckets, keeping
 * each bucket's own most-recent-first order.
 */
function groupByDay(edges) {
  const groups = [];
  let current = null;

  edges.forEach(({ node }) => {
    const day = moment(node.timestamp).format("YYYY-MM-DD");
    if (!current || current.day !== day) {
      current = { day, date: moment(node.timestamp), entries: [] };
      groups.push(current);
    }
    current.entries.push(node);
  });

  return groups;
}

function SongHistoryPage() {
  const { data, refetch } = useQuery(pageQuery, { fetchPolicy: "no-cache" });
  const { song } = useNowPlaying();

  useEffect(() => {
    refetch();
  }, [song, refetch]);

  if (!data) return <Loader />;

  const groups = groupByDay(data.allHistory.edges);

  return (
    <div className={`container ${styles.page}`}>
      <Seo title="History" />
      <header className={styles.header}>
        <PageHeader>Song History</PageHeader>
        <p>Everything played on air, most recent first.</p>
      </header>

      {groups.length ? (
        groups.map((group) => (
          <section key={group.day} className={styles.dayGroup}>
            <div className={styles.dayHead}>
              <Link
                href={`/history/${group.date.year()}/${
                  group.date.month() + 1
                }/${group.date.date()}/`}
                className={styles.dayLabel}
              >
                {group.date.format("dddd, MMMM D, YYYY")}
              </Link>
              <span className={styles.dayCount}>
                {group.entries.length} song
                {group.entries.length === 1 ? "" : "s"}
              </span>
            </div>
            <ul className={styles.list}>
              {group.entries.map((entry, i) => (
                <li key={i} className={styles.row}>
                  <span className={styles.time}>
                    {moment(entry.timestamp).format("h:mma")}
                  </span>
                  <span className={styles.song}>{entry.song}</span>
                </li>
              ))}
            </ul>
          </section>
        ))
      ) : (
        <p className={styles.empty}>There&apos;s nothing!</p>
      )}
    </div>
  );
}

export default SongHistoryPage;

const pageQuery = gql`
  query AllSongHistory {
    allHistory {
      edges {
        node {
          song
          timestamp
        }
      }
    }
  }
`;
